import Model from "./Model.ts";
import Application from "./Application.ts";
import {PaginatedData} from "../types/PaginatedData.ts";
import {useCookies} from "../composables/useCookies.ts";
import {cleanQuery} from "../utils";
import api from "../api.ts";

export class CallbackURL extends Model {
    private _title: string;
    private _description: string;
    private _url: string;
    private _type: "dev" | "beta" | "prod";
    private _application: Application;
    private _status: number;

    private _created_at: Date;
    private _updated_at: Date;

    public static async fetch({
                                  page,
                                  itemsPerPage,
                                  sortBy,
                                  search
                              }): Promise<[boolean, PaginatedData<CallbackURL>, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const cleanedQuery: string = cleanQuery(search || '');
            const response = await api.get(
                `/api/v4/callback-urls?page=${page}&limit=${itemsPerPage}&sort=${sortBy}&search=${cleanedQuery}`,
                {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    }
                });

            const rawData = response.data;

            const data: PaginatedData<CallbackURL> = {
                ...rawData,
                data: rawData.data.map((url: any) => CallbackURL.fromJson(url))
            }

            return [true, data];
        } catch (error) {
            return [false, null, error];
        }
    }

    public static async create(values: ICallbackURLForm): Promise<[boolean, CallbackURL, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(
                `/api/v4/callback-urls`,
                values,
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            const callbackURL: CallbackURL = CallbackURL.fromJson(response.data);
            return [true, callbackURL];
        } catch (error) {
            return [false, null, error];
        }
    }

    public static async getCallbackURLById(id: string): Promise<[boolean, CallbackURL, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.get(`/api/v4/callback-urls/${id}`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenCookie}`,
                },
            })

            const callbackURL: CallbackURL = CallbackURL.fromJson(response.data);
            return [true, callbackURL];
        } catch (error) {
            return [false, null, error];
        }
    }

    public static fromJson(json: any): CallbackURL {
        return new CallbackURL(
            json.id,
            json.title,
            json.description,
            json.url,
            json.type,
            json.application,
            json.status,
            json.created_at,
            json.updated_at
        )
    }

    constructor(
        id: string,
        title: string,
        description: string,
        url: string,
        type: "dev" | "beta" | "prod",
        application: any,
        status: number,
        created_at: string,
        updated_at: string,
    ) {
        super(id);
        this._title = title;
        this._description = description;
        this._url = url;
        this._type = type;
        this._application = Application.fromJson(application);
        this._status = status;
        this._created_at = new Date(created_at);
        this._updated_at = new Date(updated_at);
    }

    public get title(): string {
        return this._title;
    }

    public get description(): string {
        return this._description;
    }

    public get url(): string {
        return this._url;
    }

    public get type(): "dev" | "beta" | "prod" {
        return this._type;
    }

    public get application(): Application {
        return this._application;
    }

    public get status(): number {
        return this._status;
    }

    public get created_at(): Date {
        return this._created_at;
    }

    public get updated_at(): Date {
        return this._updated_at;
    }

    public override toJSON() {
        return {
            "id": this.id,
            "title": this._title,
            "description": this._description,
            "url": this._url,
            "type": this._type,
            "application": this._application.toJSON(),
            "status": this._status,
            "created_at": this._created_at,
            "updated_at": this._updated_at,
        }
    }

    public async update(values: ICallbackURLForm): Promise<[boolean, CallbackURL, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.patch(
                `/api/v4/callback-urls/${this.id}`,
                values,
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            // Update local instance with the response data
            this._title = response.data.title;
            this._description = response.data.description;
            this._url = response.data.url;
            this._type = response.data.type;
            this._status = response.data.status;
            this._created_at = new Date(response.data.created_at);
            this._updated_at = new Date(response.data.updated_at);

            return [true, this];
        } catch (error) {
            return [false, null, error];
        }
    }

    public async destroy(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            // Send DELETE request to the server
            await api.delete(`/api/v4/callback-urls/${this.id}`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenCookie}`,
                },
            });

            return [true];
        } catch (error) {
            return [false, error];
        }
    }

    public async activate(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api.put(
                `/api/v4/callback-urls/${this.id}/activate`, {}, {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            return [true];
        } catch (error) {
            return [false, error];
        }
    }

    public async deactivate(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api.put(
                `/api/v4/callback-urls/${this.id}/deactivate`, {}, {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            return [true];
        } catch (error) {
            return [false, error];
        }
    }
}

export interface ICallbackURLForm {
    title: string;
    description: string;
    url: string;
    app: string;
    type: "dev" | "beta" | "prod";
    application: string;
    created_by?:string
}